/**
 * Two-option view-mode toggle (e.g. Grid / List). Pair with `useViewMode`
 * from ./useViewMode for per-page localStorage persistence.
 */
import { LayoutGrid, List, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/contexts/LanguageContext'
import type { ViewMode } from './useViewMode'

type Props = {
  value: ViewMode
  onChange: (mode: ViewMode) => void
  /** Override the default Grid + List labels (rare). */
  options?: Array<{ value: ViewMode; label: string; icon: LucideIcon }>
  className?: string
}

const DEFAULT_OPTIONS: Array<{ value: ViewMode; label: string; icon: LucideIcon }> = [
  { value: 'grid', label: 'Grid', icon: LayoutGrid },
  { value: 'list', label: 'List', icon: List },
]

export function ViewToggle({ value, onChange, options = DEFAULT_OPTIONS, className }: Props) {
  const { t } = useLanguage()
  return (
    <div
      className={cn(
        'inline-flex rounded-md border bg-background p-0.5',
        className,
      )}
      role="group"
      aria-label={t('admin.aria.viewMode', 'View mode')}
    >
      {options.map((opt) => {
        const Icon = opt.icon
        const isActive = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive
                ? 'bg-muted text-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground',
            )}
            aria-pressed={isActive}
            title={opt.label + ' view'}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{opt.label}</span>
          </button>
        )
      })}
    </div>
  )
}
